import { ROUTE_PATHS } from '@constants/routes';
import { useLocalization } from '@lib/localization';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton, { type ListItemButtonProps } from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import { styled } from '@mui/material/styles';
import { usePermission } from '@store/hooks';
import {
  AngleDownIcon, CogIcon, FileAltIcon, HomeIcon, MapMarkerIcon, MobileAltIcon, ProjectDiagramIcon, ServerIcon,
  SlidersHIcon, StampIcon, UserFriendsIcon, UsersCogIcon, UsersIcon,
} from 'react-line-awesome';
import { Fragment, useState } from 'react';
import { Link, useLocation, type LinkProps } from 'react-router-dom';

const navButtonStyles = {
  padding: '2px !important',
  height: '38px',
};

const ListItemButtonLink = styled(ListItemButton)<ListItemButtonProps & LinkProps>(() => navButtonStyles);

/** A section header toggles its own list instead of navigating, so it carries no link props. */
const SectionButton = styled(ListItemButton)(() => navButtonStyles);

const StyledStack = styled(Stack)({
  flexGrow: 1,
  padding: '8px',
  justifyContent: 'space-between',
});

const StyledList = styled(List)({
  gap: '0.375rem',
  padding: 0,
});

const listItemIconStyles = {
  marginLeft: '6px',
  marginRight: '6px',
  width: '28px',
  height: '28px',
  backgroundColor: '#26272F',
  borderRadius: '.375rem',
  marginInlineEnd: '.325rem',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  border: '1px solid rgb(54, 56, 67)',
  '& i': {
    fontSize: '18px',
    color: '#B5B7C8'
  },
};

const secondaryListItemIconStyles = {
  marginLeft: '6px',
  marginRight: '6px',
  width: '28px',
  height: '28px',
  marginInlineEnd: '.325rem',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  '& i': {
    fontSize: '18px',
    color: '#B5B7C8'
  },
};

const subListStyles = {
  ml: '20px',
  pl: '14px',
  borderLeft: '1px solid',
  borderColor: 'divider',
  gap: '2px',
  py: '2px',
};

const subItemStyles = {
  height: '32px',
  '& .MuiListItemText-primary': { fontSize: 13.5 },
};

/** Points down while the section is open, and turns a quarter turn to the right when closed. */
const chevronStyles = (open: boolean) => ({
  display: 'flex',
  marginRight: '10px',
  transition: 'transform 0.2s',
  transform: open ? 'none' : 'rotate(-90deg)',
  '& i': {
    fontSize: '16px',
    color: '#B5B7C8',
  },
});

interface NavLink {
  text: string;
  icon: React.ReactNode;
  url: string;
  /** Sibling sections that belong to the same entry, e.g. search results under the tree. */
  alsoMatch?: string[];
}

/**
 * A heading over the pages that live under it. It is no destination of its own — clicking
 * it opens or closes the group — so it carries an id instead of a url: both its open state
 * and whether it reads as active are answered by its children.
 */
interface NavSection {
  text: string;
  icon: React.ReactNode;
  id: string;
  children: NavLink[];
}

type NavItem = NavLink | NavSection;

const isSection = (item: NavItem): item is NavSection => 'children' in item;

const MenuContent: React.FC<{ toggleDrawer?: () => void }> = ({ toggleDrawer }) => {
  const { can, canInAnyTeam } = usePermission();

  const { t } = useLocalization();
  const location = useLocation();
  const currentPath = location.pathname;

  // Only the sections the user has actually clicked. Everything else answers from the
  // route, so arriving on a child page from anywhere shows the section already open.
  const [toggledSections, setToggledSections] = useState<Record<string, boolean>>({});

  // The menu marks the section you are in, not the page: a folder, a project inside it or a
  // search result all belong to the same entry. Home is matched exactly — as a prefix it
  // would own every route in the app.
  const isSectionActive = (url: string, alsoMatch: string[] = []): boolean =>
    [url, ...alsoMatch].some((section) => section === ROUTE_PATHS.HOME
      ? currentPath === section
      : currentPath === section || currentPath.startsWith(`${section}/`));

  /** A heading stands for its pages, so it is the current one whenever any of them is. */
  const hasActiveChild = (section: NavSection): boolean =>
    section.children.some((child) => isSectionActive(child.url, child.alsoMatch));

  // A section holding the current page starts open; once the user has toggled it, their
  // choice wins, so collapsing the section you are in keeps it collapsed.
  const isSectionOpen = (section: NavSection): boolean => toggledSections[section.id] ?? hasActiveChild(section);

  const toggleSection = (section: NavSection) => setToggledSections((toggled) => ({
    ...toggled,
    [section.id]: !isSectionOpen(section),
  }));

  // Teams and Users are gated separately, so the section carries whatever the caller may
  // see, and is dropped altogether when that is nothing rather than opening onto an empty
  // list.
  const administrationItems: NavLink[] = [
    ...(canInAnyTeam('team:get')
      ? [{ text: t('Common_Teams'), icon: <UserFriendsIcon />, url: ROUTE_PATHS.TEAMS }]
      : []
    ),
    ...(can('user:get')
      ? [{ text: t('Common_Users'), icon: <UsersIcon />, url: ROUTE_PATHS.USERS }]
      : []
    ),
  ];

  const mainListItems: NavItem[] = [
    { text: t('Common_Home'), icon: <HomeIcon />, url: ROUTE_PATHS.HOME },
    // Search results live under /projects while the tree itself is under /folders.
    { text: t('Common_Projects'), icon: <ProjectDiagramIcon />, url: ROUTE_PATHS.FOLDERS, alsoMatch: [ROUTE_PATHS.PROJECTS] },
    ...(administrationItems.length
      ? [{
        id: 'administration',
        text: t('Administration_Section'),
        icon: <UsersCogIcon />,
        children: administrationItems,
      }]
      : []
    ),
    // Gated on reading, not authoring: every team role carries `poi_type:get`, and an
    // INSPECTOR or ANALYST has reason to look up what a type asks for. The page itself is
    // already read-only for them — the create button needs `poi_type:post`, and every
    // control on the detail page is gated on that type's own `myPermissions`. App settings are a MANAGER's and an admin's only: the
    // other roles never read a team's layer, their devices get the resolved values. The
    // report's brand is a MANAGER's and an admin's too: nobody picks a brand, the server puts
    // it on a report by itself. Report designs are gated on authoring: every team role can
    // read its team's designs, but a reader meets them where a report is downloaded, and this
    // page is for changing them. The brand stands before the designs: a logo and a name on
    // the layouts that come with the product is the first thing asked of a report, and
    // composing a layout of one's own is for when that is not enough.
    ...(canInAnyTeam('poi_type:get') || canInAnyTeam('app_settings:get') || canInAnyTeam('report_design:put')
      || canInAnyTeam('report_branding:patch')
      ? [{
        id: 'customizations',
        text: t('Customization_Section'),
        icon: <SlidersHIcon />,
        children: [
          ...(canInAnyTeam('poi_type:get')
            ? [{ text: t('Customization_CustomPoi'), icon: <MapMarkerIcon />, url: ROUTE_PATHS.CUSTOMIZATIONS_POI_TYPES }]
            : []
          ),
          ...(canInAnyTeam('app_settings:get')
            ? [{ text: t('AppSettings_Title'), icon: <MobileAltIcon />, url: ROUTE_PATHS.CUSTOMIZATIONS_APP_SETTINGS }]
            : []
          ),
          ...(canInAnyTeam('report_branding:patch')
            ? [{ text: t('ReportBranding_Title'), icon: <StampIcon />, url: ROUTE_PATHS.CUSTOMIZATIONS_REPORT_BRANDING }]
            : []
          ),
          ...(canInAnyTeam('report_design:put')
            ? [{ text: t('ReportDesign_Title'), icon: <FileAltIcon />, url: ROUTE_PATHS.CUSTOMIZATIONS_REPORT_DESIGNS }]
            : []
          ),
        ],
      }]
      : []
    ),
  ];

  const secondaryListItems: NavLink[] = [
    ...(can('server_config:get')
      ? [{ text: t('Common_ServerConfig'), icon: <ServerIcon />, url: ROUTE_PATHS.SERVER_CONFIG }]
      : []
    ),
    { text: t('Common_Settings'), icon: <CogIcon />, url: ROUTE_PATHS.SETTINGS },
  ].filter(Boolean);

  return (
    <StyledStack>
      <StyledList dense>
        {mainListItems.map((item, index) => (
          <Fragment key={index}>
            {isSection(item) ? (
              <>
                <ListItem disablePadding>
                  <SectionButton
                    selected={hasActiveChild(item)}
                    onClick={() => toggleSection(item)}
                    aria-expanded={isSectionOpen(item)}
                  >
                    <ListItemIcon sx={listItemIconStyles}>
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText primary={item.text} />
                    <Box component="span" sx={chevronStyles(isSectionOpen(item))}>
                      <AngleDownIcon />
                    </Box>
                  </SectionButton>
                </ListItem>
                <Collapse in={isSectionOpen(item)} timeout="auto" unmountOnExit>
                  <StyledList dense sx={subListStyles}>
                    {item.children.map((child, childIndex) => (
                      <ListItem key={childIndex} disablePadding>
                        <ListItemButtonLink
                          component={Link}
                          to={child.url}
                          selected={isSectionActive(child.url, child.alsoMatch)}
                          onClick={toggleDrawer}
                          sx={subItemStyles}
                        >
                          <ListItemIcon sx={secondaryListItemIconStyles}>
                            {child.icon}
                          </ListItemIcon>
                          <ListItemText primary={child.text} />
                        </ListItemButtonLink>
                      </ListItem>
                    ))}
                  </StyledList>
                </Collapse>
              </>
            ) : (
              <ListItem disablePadding>
                <ListItemButtonLink
                  component={Link}
                  to={item.url}
                  selected={isSectionActive(item.url, item.alsoMatch)}
                  onClick={toggleDrawer}
                >
                  <ListItemIcon sx={listItemIconStyles}>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText primary={item.text} />
                </ListItemButtonLink>
              </ListItem>
            )}
          </Fragment>
        ))}
      </StyledList>
      <StyledList dense sx={{ gap: '.1rem' }}>
        {secondaryListItems.map((item, index) => (
          <ListItem key={index} disablePadding>
            <ListItemButtonLink
              component={Link}
              to={item.url}
              selected={isSectionActive(item.url, item.alsoMatch)}
              onClick={toggleDrawer}
            >
              <ListItemIcon sx={secondaryListItemIconStyles}>
                {item.icon}
              </ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItemButtonLink>
          </ListItem>
        ))}
      </StyledList>
    </StyledStack>
  );
};

export default MenuContent;